import { useState, useMemo, useCallback, useEffect } from "react";
import { rmsClient } from "@/shared/api/clients";
import type {
  UsageDataPoint,
  ReportActivityData,
  PipelineData,
  StationData,
  AnalyticsFiltersState,
  KPIData,
  PerformanceData,
  ErrorData,
  AnalyticsApiResponse,
} from "../types";

function getDateRange(filters: AnalyticsFiltersState): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const endDate = now.toISOString();

  switch (filters.timeRange) {
    case "today": {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return { startDate: start.toISOString(), endDate };
    }
    case "last7": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start.toISOString(), endDate };
    }
    case "last30": {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    case "custom": {
      if (filters.customStartDate && filters.customEndDate) {
        return {
          startDate: filters.customStartDate,
          endDate: filters.customEndDate,
        };
      }
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    default: {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
  }
}

function defaultGroupBy(
  timeRange: AnalyticsFiltersState["timeRange"],
): AnalyticsFiltersState["groupBy"] {
  switch (timeRange) {
    case "today":
    case "last7":
      return "day";
    case "last30":
      return "week";
    default:
      return "day";
  }
}

function csvEscape(val: string | number): string {
  const str = String(val);
  return str.includes(",") || str.includes('"')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
}

const EMPTY_KPI: KPIData = {
  totalReports: 0,
  successRate: 0,
  avgGenerationTimeSec: 0,
  totalHoursSaved: 0,
  totalSessions: 0,
  totalEdits: 0,
};

const EMPTY_PIPELINE: PipelineData = { created: 0, completed: 0, failed: 0, retried: 0, transcriptionsFailed: 0 };

const EMPTY_ERRORS: ErrorData = {
  connectionFailures: 0,
  operationErrors: 0,
  sessionTimeouts: 0,
  transcriptionFailures: 0,
};

const EMPTY_PERFORMANCE: PerformanceData = {
  avgReportGenSec: 0,
  avgPdfGenSec: 0,
  avgEditingTimeSec: 0,
  avgVideosPerReport: 0,
  totalRetries: 0,
};

export const useAnalyticsData = () => {
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    timeRange: "last30",
    groupBy: "week",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsApiResponse | null>(null);
  const [allStations, setAllStations] = useState<StationData[]>([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange(filters);

      const params: Record<string, string> = {
        startDate,
        endDate,
        groupBy: filters.groupBy,
      };

      if (filters.stationId) {
        params.stationId = filters.stationId;
      }

      const response = await rmsClient.get<AnalyticsApiResponse>("/analytics", {
        params,
      });

      setData(response.data);
      if (!filters.stationId && response.data.stationBreakdown.length > 0) {
        setAllStations(response.data.stationBreakdown);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load analytics data";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters.timeRange, filters.stationId, filters.groupBy, filters.customStartDate, filters.customEndDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const usageOverTime: UsageDataPoint[] = useMemo(
    () => data?.usageOverTime ?? [],
    [data],
  );

  const reportActivity: ReportActivityData[] = useMemo(
    () => data?.reportActivity ?? [],
    [data],
  );

  const pipeline: PipelineData = useMemo(
    () => data?.pipeline ?? EMPTY_PIPELINE,
    [data],
  );

  const stationBreakdown: StationData[] = useMemo(
    () => data?.stationBreakdown ?? [],
    [data],
  );

  const kpiData: KPIData = useMemo(() => data?.kpi ?? EMPTY_KPI, [data]);

  const performance: PerformanceData = useMemo(
    () => data?.performance ?? EMPTY_PERFORMANCE,
    [data],
  );

  const errors: ErrorData = useMemo(
    () => data?.errors ?? EMPTY_ERRORS,
    [data],
  );

  const maxUsage = useMemo(
    () =>
      usageOverTime.length > 0
        ? Math.max(...usageOverTime.map((d) => d.completed + d.failed))
        : 0,
    [usageOverTime],
  );

  const handleTimeRangeChange = useCallback(
    (timeRange: AnalyticsFiltersState["timeRange"]) => {
      setFilters((prev) => ({
        ...prev,
        timeRange,
        groupBy: defaultGroupBy(timeRange),
      }));
    },
    [],
  );

  const handleGroupByChange = useCallback(
    (groupBy: AnalyticsFiltersState["groupBy"]) => {
      setFilters((prev) => ({ ...prev, groupBy }));
    },
    [],
  );

  const handleStationChange = useCallback((stationId?: string) => {
    setFilters((prev) => ({ ...prev, stationId }));
  }, []);

  const handleCustomDateChange = useCallback(
    (startDate: string, endDate: string) => {
      setFilters((prev) => ({
        ...prev,
        customStartDate: startDate,
        customEndDate: endDate,
      }));
    },
    [],
  );

  const exportToCSV = useCallback(() => {
    const sections: string[] = [];

    sections.push(
      ["KPI Summary", "Metric,Value",
        `Total Reports,${kpiData.totalReports}`,
        `Success Rate,${kpiData.successRate}%`,
        `Avg Generation Time,${kpiData.avgGenerationTimeSec}s`,
        `Total Hours Saved,${kpiData.totalHoursSaved}`,
        `Total Sessions,${kpiData.totalSessions}`,
        `Total Edits,${kpiData.totalEdits}`,
      ].join("\n"),
    );

    sections.push(
      ["Usage Over Time", "Date,Completed,Failed",
        ...usageOverTime.map((d) => `${csvEscape(d.label)},${d.completed},${d.failed}`),
      ].join("\n"),
    );

    sections.push(
      ["Report Pipeline", "Stage,Count",
        `Created,${pipeline.created}`,
        `Completed,${pipeline.completed}`,
        `Failed,${pipeline.failed}`,
        `Retried,${pipeline.retried}`,
        `Transcription Failures,${pipeline.transcriptionsFailed}`,
      ].join("\n"),
    );

    sections.push(
      ["Performance", "Metric,Value",
        `Avg Report Generation,${performance.avgReportGenSec}s`,
        `Avg PDF Generation,${performance.avgPdfGenSec}s`,
        `Avg Editing Time,${performance.avgEditingTimeSec}s`,
        `Avg Videos Per Report,${performance.avgVideosPerReport}`,
        `Total Retries,${performance.totalRetries}`,
      ].join("\n"),
    );

    sections.push(
      ["Report Activity", "Action,Count",
        ...reportActivity.map((d) => `${csvEscape(d.label)},${d.count}`),
      ].join("\n"),
    );

    sections.push(
      ["Station Breakdown", "Station ID,Station Name,Report Count",
        ...stationBreakdown.map((d) => `${csvEscape(d.stationId)},${csvEscape(d.stationName)},${d.reportCount}`),
      ].join("\n"),
    );

    sections.push(
      ["Errors", "Metric,Count",
        `Connection Failures,${errors.connectionFailures}`,
        `Operation Errors,${errors.operationErrors}`,
        `Session Timeouts,${errors.sessionTimeouts}`,
        `Transcription Failures,${errors.transcriptionFailures}`,
      ].join("\n"),
    );

    const csvContent = sections.join("\n\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `analytics_export_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  }, [kpiData, usageOverTime, pipeline, reportActivity, stationBreakdown, performance, errors]);

  return {
    filters,
    handleTimeRangeChange,
    handleGroupByChange,
    handleStationChange,
    handleCustomDateChange,
    usageOverTime,
    reportActivity,
    pipeline,
    stationBreakdown,
    allStations,
    kpiData,
    performance,
    errors,
    maxUsage,
    exportToCSV,
    loading,
    error,
    refetch: fetchAnalytics,
  };
};
